import { Observable } from 'rxjs';
import { DocumentTag } from './types.domain';

/**
 * Per-entity trust deed conditions.
 *
 * A trust deed used to be a single global `trustdeed` document tag, emitted only when the primary
 * entity was a trust, so every deed on an application collapsed into one box and nothing said
 * which deed belonged to which trust. Each entity that needs a deed now gets its own condition,
 * identified by a value derived from the entity rather than from its name, so a deed stays
 * attached when the entity is renamed.
 *
 * The label and reason line are built here rather than at each call site so the submission wizard
 * and the Documents tab cannot drift apart — the broker sees identical wording in both places.
 */

export type RequiredTrustDeedKind = 'trust-entity' | 'non-beneficial-shareholding';

export interface RequiredTrustDeed {
  /** Stable `DocumentTag.value`; also the Azure blob tag a deed uploaded here carries. */
  value: string;
  /** Row title on the Documents tab and box title in the wizard. */
  label: string;
  /** Short line under the label saying why the deed is required. */
  reason: string;
  /**
   * The entity the deed is analysed against. For a trust this is the trust itself; for a
   * non-beneficial shareholding it is the company whose shares are held, since the deed attaches
   * to that company's FrankieOne organisation.
   */
  applicationCommercialEntityId: number;
  kind: RequiredTrustDeedKind;
}

export type RequiredTrustDeedsFn = (applicationId: number) => Observable<RequiredTrustDeed[]>;

/** Legacy single-box tag value. Deeds uploaded before per-entity conditions still carry it. */
export const LEGACY_TRUST_DEED_VALUE = 'trustdeed';

const TRUST_ENTITY_PREFIX = 'trustdeed_e';
const NON_BENEFICIAL_PREFIX = 'trustdeed_nb';

/**
 * Underscores only, no hyphens: uploadFile writes every tag on a document as an Azure blob metadata
 * *name*, and metadata names must be valid C# identifiers, so a hyphen fails the upload with
 * InvalidMetadata. Lower-cased because both the portal and the approval-notice builder lower-case
 * tag values before matching them.
 */
const normaliseValue = (value: string): string => value.toLowerCase().replace(/[^a-z0-9_]/g, '');

export const trustEntityDeedValue = (applicationCommercialEntityId: number): string =>
  normaliseValue(`${TRUST_ENTITY_PREFIX}${applicationCommercialEntityId}`);

/**
 * Keyed by the FrankieOne entity id of the shareholder rather than by our stakeholder row id,
 * because re-running KYB replaces the stakeholder rows and would otherwise orphan an uploaded deed.
 */
export const nonBeneficialDeedValue = (
  applicationCommercialEntityId: number,
  shareholderFrankieOneEntityId: string,
): string =>
  normaliseValue(`${NON_BENEFICIAL_PREFIX}${applicationCommercialEntityId}_${shareholderFrankieOneEntityId}`);

/**
 * Hyphens are accepted as well as underscores so that conditions written before the separator
 * changed are still recognised, and so are dropped by the next sync rather than lingering.
 */
export const isTrustDeedConditionValue = (value: string | null | undefined): boolean =>
  /^trustdeed[-_](e|nb)/.test((value ?? '').toLowerCase());

/** True for the pre-per-entity tag, which keeps its own generic box on older applications. */
export const isLegacyTrustDeedValue = (value: string | null | undefined): boolean =>
  (value ?? '').toLowerCase() === LEGACY_TRUST_DEED_VALUE;

export const isAnyTrustDeedValue = (value: string | null | undefined): boolean =>
  isTrustDeedConditionValue(value) || isLegacyTrustDeedValue(value);

/** Drops a trailing `.0` so a whole percentage reads "25%" rather than "25.0%". */
export const formatOwnershipPct = (pct: number): string =>
  Number.isFinite(pct) ? String(Number(pct.toFixed(2))) : '';

/**
 * ABR legal names for trusts are already in trustee form — "The Trustee for the Smith Family
 * Trust" — so the trustee wording is only added when the name does not already carry it. Without
 * this a real entity reads "the Trustee of the The Trustee for the Smith Family Trust".
 */
const trusteeName = (entityLegalName: string): string => {
  const name = entityLegalName.trim();
  return /^the\s+trustee\b/i.test(name) ? name : `The Trustee of the ${name}`;
};

export const trustEntityDeedLabel = (entityLegalName: string): string =>
  `Trust Deed for "${trusteeName(entityLegalName)}"`;

export const trustEntityDeedReason = (entityLegalName: string): string =>
  `${entityLegalName} is a trust`;

export const nonBeneficialDeedLabel = (
  personName: string,
  ownershipPct: number,
  companyName: string,
): string =>
  `Trust Deed for ${personName}'s ${formatOwnershipPct(ownershipPct)}% shareholding in ${companyName} ` +
  `that is held non-beneficially`;

export const nonBeneficialDeedReason = (personName: string): string =>
  `Our records show ${personName} holds these shares non-beneficially`;

/**
 * The descriptor as it is stored on `Application.DocumentTypes` and rendered by the standard
 * conditions table. `trustDeedCondition` marks it for re-emission as a standard condition, the
 * same way `conditionalAlert` marks a credit-alert condition.
 *
 * The description repeats the label because the approval notice prints only the description.
 */
export const requiredTrustDeedToDocumentTag = (deed: RequiredTrustDeed): DocumentTag => ({
  value: deed.value,
  type: deed.label,
  description: deed.label,
  required: true,
  standardCondition: true,
  trustDeedCondition: true,
  applicationCommercialEntityId: deed.applicationCommercialEntityId,
});

/** Recovers a descriptor from a stored tag, so the wizard can render boxes from the same source. */
export const documentTagToRequiredTrustDeed = (tag: DocumentTag): RequiredTrustDeed | null => {
  if (!tag?.trustDeedCondition || tag.applicationCommercialEntityId == null) {
    return null;
  }
  return {
    value: tag.value,
    label: tag.type,
    reason: tag.description ?? '',
    applicationCommercialEntityId: tag.applicationCommercialEntityId,
    kind: /^trustdeed[-_]nb/.test(tag.value.toLowerCase())
      ? 'non-beneficial-shareholding'
      : 'trust-entity',
  };
};
